import React, { useState, useEffect, useCallback } from 'react';
import { useMobileGridView } from '../hooks/useMobileGridView';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { CountBand } from '../components/ui/CountBand';
import { apiGet, apiList, apiUpdate } from '../api/apiClient';
import { useAuth } from '../hooks/useAuth';
import { CameraSetupWizard } from '../components/cameras/CameraSetupWizard';
import { CameraGridView } from '../components/cameras/CameraGridView';
import { AddCameraModal } from '../components/cameras/AddCameraModal';
import { CameraPlayer } from '../components/cameras/CameraPlayer';
import { HlsVideo } from '../components/cameras/HlsVideo';
import { DeviceActionButton, DeviceStatusIcon, deviceTypeIcon, ProviderPill } from '../components/access/DeviceVisuals';
import { AuthorizedResourceSetupModal, AuthorizedResourceSelection } from '../components/integrations/AuthorizedResourceSetupModal';
import { UniFiCameraSelectionModal } from '../components/cameras/UniFiCameraSelectionModal';
import { recordFreshness } from '../utils/recordFreshness';
import { cameraSystemForProperty } from '../utils/cameraSystem';

const API_BASE = window.location.hostname.startsWith('shadow.')
  ? 'https://shadow.api.intrlock.io'
  : (process.env.REACT_APP_API_URL || 'https://api.intrlock.io');
function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('intrlock_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export const Cameras: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cameras, setCameras] = useState<any[]>([]);
  const [properties, setProperties] = useState<Record<number, any>>({});
  const [propertyList, setPropertyList] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [inventoryWarning, setInventoryWarning] = useState('');
  const [providerInventoryWarning, setProviderInventoryWarning] = useState('');
  const [primaryInventoryUnavailable, setPrimaryInventoryUnavailable] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => (localStorage.getItem('cameras_view_mode') as 'table' | 'grid') || 'table');
  useMobileGridView(setViewMode, 'grid');
  const [pageTab, setPageTab] = useState<'systems' | 'cameras'>('cameras');
  const [savedViews, setSavedViews] = useState<Array<{ id: string; name: string; cameraIds: string[] }>>(() => { try { return JSON.parse(localStorage.getItem('camera_saved_views') || '[]'); } catch { return []; } });
  const [activeViewId, setActiveViewId] = useState(() => localStorage.getItem('camera_active_view') || 'all');
  const [showViewEditor, setShowViewEditor] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewCameraIds, setViewCameraIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showAuthorizedSetup, setShowAuthorizedSetup] = useState(false);
  const [authorizedCameraSelection, setAuthorizedCameraSelection] = useState<AuthorizedResourceSelection | null>(null);
  const [showOnvifModal, setShowOnvifModal] = useState(false);
  const [onvifPropertyId, setOnvifPropertyId] = useState<string>('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Camera systems — cloud-hosted camera providers (currently Turing only).
  // Each row = one access token attached to one property.
  const [turingSystems, setTuringSystems] = useState<any[]>([]);
  const [unifiSystems, setUnifiSystems] = useState<any[]>([]);
  const [liveCameraStates, setLiveCameraStates] = useState<Record<string, boolean>>({});
  const [checkedCameraSystems, setCheckedCameraSystems] = useState<Set<string>>(new Set());
  const [showAddTuring, setShowAddTuring] = useState(false);
  const [viewPersistenceError, setViewPersistenceError] = useState('');

  const persistCameraViews = useCallback(async (views: typeof savedViews, active: string) => {
    localStorage.setItem('camera_saved_views', JSON.stringify(views));
    localStorage.setItem('camera_active_view', active);
    const userId = (user as any)?.uuid || user?.id;
    if (!userId) return;
    try {
      const fresh = await apiGet('users', userId);
      const raw = fresh?.notification_preferences_json;
      const preferences = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
      await apiUpdate('users', userId, {
        notification_preferences_json: {
          ...preferences,
          camera_saved_views: views,
          camera_active_view: active,
        },
      });
      const verified = await apiGet('users', userId);
      const verifiedRaw = verified?.notification_preferences_json;
      const verifiedPrefs = typeof verifiedRaw === 'string' ? JSON.parse(verifiedRaw || '{}') : (verifiedRaw || {});
      setSavedViews(Array.isArray(verifiedPrefs.camera_saved_views) ? verifiedPrefs.camera_saved_views : views);
      setActiveViewId(verifiedPrefs.camera_active_view || active);
      setViewPersistenceError('');
    } catch (error: any) {
      setViewPersistenceError(error?.message || 'Camera view could not be saved to your account. It remains available on this browser.');
    }
  }, [user]);

  useEffect(() => {
    const userId = (user as any)?.uuid || user?.id;
    if (!userId) return;
    (async () => {
      try {
        const fresh = await apiGet('users', userId);
        const raw = fresh?.notification_preferences_json;
        const preferences = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
        const serverViews = preferences.camera_saved_views;
        if (Array.isArray(serverViews)) {
          setSavedViews(serverViews);
          setActiveViewId(preferences.camera_active_view || 'all');
          localStorage.setItem('camera_saved_views', JSON.stringify(serverViews));
          localStorage.setItem('camera_active_view', preferences.camera_active_view || 'all');
        } else if (savedViews.length) {
          await persistCameraViews(savedViews, activeViewId);
        }
      } catch (error: any) {
        setViewPersistenceError(error?.message || 'Account camera views could not be loaded.');
      }
    })();
    // Initial migration/load only; subsequent writes call persistCameraViews.
  }, [(user as any)?.uuid, user?.id]);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('setup') !== 'turing') return;
    setShowAddTuring(true);
    navigate('/cameras', { replace: true });
  }, [location.search, navigate]);
  // Active Turing playback state — overlay shown when a Turing camera
  // row is clicked. The stream URL is fetched on demand from the
  // backend, which holds the access token (never sent to the browser).
  const [turingPlay, setTuringPlay] = useState<{
    systemId: string;
    cameraId: string | number;
    provider: 'turing' | 'unifi_protect';
    streamUrl: string | null;
    error: string | null;
  } | null>(null);
  // Pending property reassignments — keyed by device id. Each entry =
  // the property_id the PM picked from the dropdown but hasn't saved yet.
  const [pendingAssignment, setPendingAssignment] = useState<Record<string, string>>({});
  const [savingAssignment, setSavingAssignment] = useState<Set<string>>(new Set());

  const fetchTuringSystems = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/integrations/turing/systems`, { headers: authHeaders() });
      if (r.ok) setTuringSystems(await r.json());
    } catch (e) { console.warn('Failed to load Turing systems:', e); }
  }, []);

  const fetchUnifiSystems = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/integrations/unifi-protect/systems`, { headers: authHeaders() });
      if (r.ok) setUnifiSystems(await r.json());
    } catch (e) { console.warn('Failed to load UniFi Protect systems:', e); }
  }, []);

  const fetchCameras = useCallback(async () => {
    try {
      const sources = await Promise.allSettled([
        apiList('devices'),
        apiList('bridge_devices'),
        apiList('properties'),
        apiList('customers'),
      ]);
      const sourceNames = ['devices', 'bridge devices', 'properties', 'customers'];
      const failedSources = sources.flatMap((result, index) => result.status === 'rejected' ? [sourceNames[index]] : []);
      setInventoryWarning(failedSources.length
        ? `Some camera inventory could not be loaded: ${failedSources.join(', ')}. Displayed results may be incomplete.`
        : '');
      setPrimaryInventoryUnavailable(sources[0].status === 'rejected');
      const [deviceData, bridgeData, propertyData, customerData] = sources.map((result) =>
        result.status === 'fulfilled' ? result.value : []
      );

        const propMap: Record<number, any> = {};
        (propertyData || []).forEach((p: any) => { propMap[p.id] = p; });
        setProperties(propMap);
        setPropertyList(propertyData || []);
        setCustomers(customerData || []);

        // Filter for camera-type devices from both regular devices and bridge devices
        const regularCameras = (deviceData || [])
          .filter((d: any) => {
            const type = (d.type || d.device_type || '').toLowerCase();
            return type === 'camera' || type === 'cam' || type === 'bridge_cam';
          })
          .map((d: any) => ({
            ...d,
            _source: 'device',
            _property_name: propMap[d.property_id]?.name || `Property #${d.property_id || '?'}`,
            _property_address: [propMap[d.property_id]?.address, propMap[d.property_id]?.city].filter(Boolean).join(', '),
          }));

        // Bridge cameras (from bridge_devices table — type = 'camera' or device_type contains 'cam')
        const bridgeCameras = (bridgeData || [])
          .filter((b: any) => {
            const type = (b.device_type || b.type || '').toLowerCase();
            return type === 'camera' || type === 'cam' || type.includes('cam');
          })
          .map((b: any) => ({
            ...b,
            _source: 'bridge',
            _property_name: propMap[b.property_id]?.name || `Property #${b.property_id || '?'}`,
            _property_address: [propMap[b.property_id]?.address, propMap[b.property_id]?.city].filter(Boolean).join(', '),
          }));

        setCameras([...regularCameras, ...bridgeCameras]);
      } catch (e) {
        console.error('Failed to load cameras:', e);
        setInventoryWarning('Camera inventory could not be loaded. Displayed results may be incomplete.');
        setPrimaryInventoryUnavailable(true);
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => { fetchCameras(); fetchTuringSystems(); fetchUnifiSystems(); }, [fetchCameras, fetchTuringSystems, fetchUnifiSystems]);

  // Provider inventory is the authority for connectivity. The devices table is
  // intentionally retained as a last-known state, but it can lag a camera that
  // has reconnected since the most recent sync.
  useEffect(() => {
    const systems = [
      ...turingSystems.map((system) => ({ ...system, _provider: 'turing', _route: 'turing' })),
      ...unifiSystems.map((system) => ({ ...system, _provider: 'unifi_protect', _route: 'unifi-protect' })),
    ];
    if (!systems.length) return;
    let cancelled = false;
    const normalize = (value: unknown) => String(value || '').trim().toLowerCase();
    (async () => {
      const entries = await Promise.all(systems.map(async (system) => {
        try {
          const response = await fetch(`${API_BASE}/integrations/${system._route}/systems/${system.id}/cameras?limit=100`, { headers: authHeaders() });
          if (!response.ok) {
            let code = '';
            try { code = String((await response.json())?.code || ''); } catch { /* non-JSON provider response */ }
            return { id: String(system.id), provider: system._provider, code, states: [] as Array<[string, boolean]> };
          }
          const payload = await response.json();
          const discovered = Array.isArray(payload) ? payload : (payload.cameras || payload.data || []);
          const states = discovered.flatMap((camera: any) => {
            const id = String(camera.id ?? camera.cameraId ?? '');
            const name = normalize(camera.name || camera.displayName || camera.modelKey);
            const state = normalize(camera.state || camera.connection_status || camera.connectionState);
            const online = camera.online === true || camera.isConnected === true || state === 'running' || state === 'online' || state === 'connected';
            const keys: string[] = [];
            if (id) keys.push(`${system._provider}:id:${id}`);
            if (name) {
              keys.push(`${system._provider}:system:${system.id}:name:${name}`);
              if (system.entity_id) keys.push(`${system._provider}:property:${system.entity_id}:name:${name}`);
            }
            return keys.map((key): [string, boolean] => [key, online]);
          });
          return { id: String(system.id), provider: system._provider, code: '', states };
        } catch (error) {
          console.warn(`Failed to load live ${system._provider} camera state:`, error);
          return { id: String(system.id), provider: system._provider, code: 'provider_unavailable', states: [] as Array<[string, boolean]> };
        }
      }));
      if (!cancelled) {
        setLiveCameraStates(Object.fromEntries(entries.flatMap(entry => entry.states)));
        setCheckedCameraSystems(new Set(entries.map(entry => entry.id)));
        const reconnect = entries.some(entry => entry.code === 'integration_reauthorization_required');
        const unavailableProviders = Array.from(new Set(entries.filter(entry => entry.code && entry.code !== 'integration_reauthorization_required').map(entry => entry.provider)));
        setProviderInventoryWarning(reconnect
          ? 'UniFi Protect must be reconnected by the console owner before live camera status and streams are available.'
          : unavailableProviders.length
            ? `Live camera status is temporarily unavailable for: ${unavailableProviders.map(provider => provider === 'unifi_protect' ? 'UniFi Protect' : 'Turing').join(', ')}.`
            : '');
      }
    })();
    return () => { cancelled = true; };
  }, [turingSystems, unifiSystems]);

  const saveAssignment = async (cameraId: string) => {
    const newPropertyId = pendingAssignment[cameraId];
    if (!newPropertyId) return;
    setSavingAssignment((prev) => { const n = new Set(prev); n.add(cameraId); return n; });
    try {
      await apiUpdate('devices', cameraId, { property_id: newPropertyId });
      // Drop the pending entry and refresh so the row picks up the new
      // _property_name + the dropdown reflects the persisted value.
      setPendingAssignment((prev) => { const n = { ...prev }; delete n[cameraId]; return n; });
      await fetchCameras();
    } catch (err) {
      console.error('Failed to reassign camera:', err);
      alert('Failed to reassign camera. Try again.');
    } finally {
      setSavingAssignment((prev) => { const n = new Set(prev); n.delete(cameraId); return n; });
    }
  };

  /// Open the preview for any camera in the grid. Turing-imported rows
  /// (acs_provider='turing') use the HlsVideo overlay + Turing's
  /// /stream-url endpoint. Other rows fall back to the legacy
  /// CameraPlayer (ONVIF / bridge cameras).
  const openCameraPreview = async (c: any) => {
    if (c.acs_provider === 'turing' || c.acs_provider === 'unifi_protect') {
      const isTuring = c.acs_provider === 'turing';
      const systems = isTuring ? turingSystems : unifiSystems;
      const property = propertyList.find((p) => String(p.id || p.uuid) === String(c.property_id));
      const customerId = c.customer_id || property?.customer_id;
      const system = cameraSystemForProperty(systems, c.property_id)
        || systems.find((s) => s.entity_type === 'customer' && String(s.entity_id) === String(customerId));
      if (!system) {
        setTuringPlay({ systemId: '', cameraId: 0, provider: c.acs_provider, streamUrl: null, error: `No ${isTuring ? 'Turing' : 'UniFi Protect'} system attached to this property.` });
        return;
      }
      // §28.518.85: pass the opaque envelope UUID; backend unwraps to plaintext
      // Turing id via KMS before calling get_hls_stream. acs_device_id is stripped
      // by the H5 response scrubber; envelope_token is the canonical handle.
      const cameraId = c.envelope_token || c.acs_device_id;
      setTuringPlay({ systemId: system.id, cameraId, provider: c.acs_provider, streamUrl: null, error: null });
      try {
        const route = isTuring ? 'turing' : 'unifi-protect';
        const r = await fetch(`${API_BASE}/integrations/${route}/systems/${system.id}/stream-url`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(isTuring ? { camera_id: cameraId, resolution: 'sub' } : { camera_id: cameraId, quality: 'low' }),
        });
        const data = await r.json();
        if (data.error === 'license_required') {
          setTuringPlay({ systemId: system.id, cameraId, provider: c.acs_provider, streamUrl: null, error: data.message || 'Camera does not have a Core AI license.' });
        } else if (data.play_url) {
          setTuringPlay({ systemId: system.id, cameraId, provider: c.acs_provider, streamUrl: data.play_url, error: null });
        } else {
          setTuringPlay({ systemId: system.id, cameraId, provider: c.acs_provider, streamUrl: null, error: 'No stream URL returned.' });
        }
      } catch (e: any) {
        setTuringPlay({ systemId: system.id, cameraId, provider: c.acs_provider, streamUrl: null, error: String(e?.message ?? e).slice(0, 200) });
      }
    } else {
      setPreviewId(String(c.id));
    }
  };

  const searchedCameras = search.trim()
    ? cameras.filter(c => {
        const q = search.toLowerCase().trim();
        const system = turingSystems.find(s => String(s.id) === String(c.acs_system_id)) || cameraSystemForProperty(turingSystems, c.property_id);
        const property = propertyList.find(p => String(p.id) === String(c.property_id) || String(p.uuid) === String(c.property_id));
        const searchable = [c.name, c.device_name, c._property_name, c._property_address, property?.name, property?.address, property?.city, property?.state, property?.zip, c.acs_provider, c.provider, c.manufacturer, c.model, c.serial_number, c.device_type, c.type, c.status, c.connection_status, c.acs_device_id, c.integration_device_id, system?.name, system?.provider, system?.system_name, 'camera'].filter(Boolean).join(' ').toLowerCase();
        return q.split(/\s+/).every(token => searchable.includes(token));
      })
    : cameras;
  const activeSavedView = savedViews.find(view => view.id === activeViewId);
  const filtered = viewMode === 'grid' && activeSavedView ? searchedCameras.filter(camera => activeSavedView.cameraIds.includes(String(camera.id))) : searchedCameras;
  // Pagination: keep the visible tile count small so we don't overload the
  // browser with concurrent HLS pipelines (§28.518.87 v2).
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, filtered.length);
  const paginated = filtered.slice(startIdx, endIdx);
  useEffect(() => { setPage(1); }, [search, viewMode]);

  // A camera is online when ANY of its live-state signals say so. The
  // legacy `status` column is a lifecycle flag (active/disabled), not
  // online/offline — checking it first wrongly marked every active
  // camera as offline. Look at `connection_status` first, then the
  // boolean `online`, then fall back to `status`.
  const cameraConnectivity = (c: any): 'online' | 'offline' | 'unknown' => {
    const provider = c.acs_provider;
    const system = provider === 'unifi_protect'
      ? unifiSystems.find((s: any) => String(s.id) === String(c.acs_system_id) || (s.entity_type === 'property' && String(s.entity_id) === String(c.property_id)) || (s.entity_type === 'customer' && String(s.entity_id) === String(c.customer_id)))
      : turingSystems.find((s: any) => String(s.id) === String(c.acs_system_id))
        || cameraSystemForProperty(turingSystems, c.property_id)
        || turingSystems.find((s: any) => s.entity_type === 'customer' && String(s.entity_id) === String(c.customer_id));
    const name = String(c.name || c.device_name || '').trim().toLowerCase();
    const externalId = String(c.acs_device_id || c.integration_device_id || '').trim();
    const liveKeys = [
      externalId && `${provider}:id:${externalId}`,
      system?.id && name && `${provider}:system:${system.id}:name:${name}`,
      c.property_id && name && `${provider}:property:${c.property_id}:name:${name}`,
    ].filter(Boolean) as string[];
    if (system) {
      if (!checkedCameraSystems.has(String(system.id))) return 'unknown';
      for (const key of liveKeys) {
        if (Object.prototype.hasOwnProperty.call(liveCameraStates, key)) return liveCameraStates[key] ? 'online' : 'offline';
      }
      // An unavailable inventory or a camera absent from it cannot prove
      // current connectivity; do not present the last-known PG value as live.
      return 'unknown';
    }
    if ((c.connection_status || '').toLowerCase() === 'online') return 'online';
    if (c.online === true) return 'online';
    if ((c.status || '').toLowerCase() === 'online') return 'online';
    if ((c.connection_status || '').toLowerCase() === 'offline' || c.online === false) return 'offline';
    return 'unknown';
  };
  const isOnline = (c: any): boolean => cameraConnectivity(c) === 'online';
  const onlineCount = filtered.filter(isOnline).length;
  const offlineCount = filtered.filter(c => cameraConnectivity(c) === 'offline').length;
  const unknownCount = filtered.length - onlineCount - offlineCount;
  const cameraMetric = (value: number) => primaryInventoryUnavailable ? '—' : value;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  return (
    <div className={`shadow-list-page has-prototype-toolbar cameras-list-page cameras-tab-${pageTab}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cameras</h1>
          <p className="text-gray-500 mt-1">Monitor all camera devices across properties</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => { setViewMode('table'); localStorage.setItem('cameras_view_mode','table'); }}
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'table' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Table</button>
            <button
              onClick={() => { setViewMode('grid'); localStorage.setItem('cameras_view_mode','grid'); }}
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'grid' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Grid</button>
          </div>
          <Button onClick={() => setShowAuthorizedSetup(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Camera
          </Button>
        </div>
      </div>
      {(inventoryWarning || providerInventoryWarning) && <div role="alert" className="mb-3 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{[inventoryWarning, providerInventoryWarning].filter(Boolean).join(' ')}</div>}
      {!primaryInventoryUnavailable && <CountBand value={filtered.length} detail={inventoryWarning ? 'Cameras loaded from available sources; inventory incomplete' : 'Cameras in the current accessible portfolio'} />}
      <nav className="person-tabs cameras-page-tabs" aria-label="Camera page sections"><button className={pageTab === 'systems' ? 'active' : ''} onClick={() => setPageTab('systems')}>Systems <b>{turingSystems.length + unifiSystems.length}</b></button><button className={pageTab === 'cameras' ? 'active' : ''} onClick={() => setPageTab('cameras')}>All Cameras <b>{cameraMetric(filtered.length)}</b></button></nav>
      <div className="prototype-toolbar cameras-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search all camera data..." />
        {viewMode === 'grid' && <label><small>Saved view</small><select value={activeViewId} onChange={e => { const active=e.target.value; setActiveViewId(active); void persistCameraViews(savedViews, active); setPage(1); }}><option value="all">All cameras</option>{savedViews.map(view => <option key={view.id} value={view.id}>{view.name} ({view.cameraIds.length})</option>)}</select></label>}
        {viewMode === 'grid' && <button className="device-sort-direction" onClick={() => { const current = savedViews.find(view => view.id === activeViewId); setViewName(current?.name || ''); setViewCameraIds(new Set(current?.cameraIds || cameras.map(camera => String(camera.id)))); setShowViewEditor(true); }}>{activeSavedView ? 'Edit view' : 'Create view'}</button>}
        <div className="device-status-summary"><span><b>{cameraMetric(onlineCount)}</b> Online</span><span><b>{cameraMetric(offlineCount)}</b> Offline</span>{!primaryInventoryUnavailable && unknownCount > 0 && <span><b>{unknownCount}</b> Unknown</span>}</div>
        <div className="prototype-view-toggle"><button className={viewMode === 'table' ? 'active' : ''} onClick={() => { setViewMode('table'); localStorage.setItem('cameras_view_mode','table'); }}>Table</button><button className={viewMode === 'grid' ? 'active' : ''} onClick={() => { setViewMode('grid'); localStorage.setItem('cameras_view_mode','grid'); }}>Grid</button></div>
        <Button onClick={() => setShowAuthorizedSetup(true)}>Add Camera</Button>
      </div>

      {/* Camera Systems — cloud providers (Turing, etc). One row per stored
          access token. Click to manage the system (sites, cameras, property
          assignment). Mirrors the Access Control Systems list pattern. */}
      {turingSystems.length > 0 && (
        <Card padding={false} className="mb-6 camera-systems-panel">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Camera Systems</h3>
              <p className="text-xs text-gray-400 mt-0.5">Cloud-hosted camera platforms. Click a system to manage cameras and property assignment.</p>
            </div>
            <Badge color="blue">{turingSystems.length} system{turingSystems.length === 1 ? '' : 's'}</Badge>
          </div>
          <div className="md:hidden grid grid-cols-1 gap-3 p-3">
            {turingSystems.map((system: any) => {
              const systemCameras = cameras.filter((camera: any) => String(camera.acs_system_id) === String(system.id) || String(camera.property_id) === String(system.entity_id) || (camera.acs_provider === 'turing' && String(camera.property_name || camera._property_name) === String(system.property_name)));
              const enabled = system.enabled !== false;
              return <article key={system.id} onClick={() => navigate(`/cameras/systems/${system.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                <div className="flex items-start justify-between mb-2 gap-2"><div className="min-w-0"><h3 className="font-semibold text-gray-900 truncate">{system.name || 'Turing Vision'}</h3></div><Badge color={enabled ? 'green' : 'gray'}>{enabled ? 'enabled' : 'disabled'}</Badge></div>
                <div className="customer-card-address"><span>Property</span><p>{system.property_name || system.entity_id?.slice(0, 8) || 'Unassigned'}</p></div>
                <div className="customer-card-contact"><span>Provider</span><strong>Turing Vision</strong><span>System</span><strong>{system.name || system.id}</strong></div>
                <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary><div><article><span>Cameras</span><strong>{cameraMetric(systemCameras.length)}</strong></article><article><span>Online</span><strong>{cameraMetric(systemCameras.filter((camera: any) => cameraConnectivity(camera) === 'online').length)}</strong></article><article><span>Offline</span><strong>{cameraMetric(systemCameras.filter((camera: any) => cameraConnectivity(camera) === 'offline').length)}</strong></article><article><span>Unknown</span><strong>{cameraMetric(systemCameras.filter((camera: any) => cameraConnectivity(camera) === 'unknown').length)}</strong></article><article><span>Added</span><strong className="metric-text-value">{system.created_at ? new Date(system.created_at).toLocaleDateString() : '—'}</strong></article></div></details>
                <div className="mobile-card-actions"><button onClick={(e) => { e.stopPropagation(); navigate(`/cameras/systems/${system.id}`); }} className="row-open">Manage <span aria-hidden="true">→</span></button></div>
              </article>;
            })}
          </div>
          <div className="hidden md:block">
          <Table
            columns={[
              { key: 'provider', header: 'Provider', render: () => <Badge color="purple">Turing Vision</Badge> },
              { key: 'property', header: 'Attached Property', render: (s: any) => s.property_name || s.entity_id?.slice(0, 8) },
              { key: 'enabled', header: 'Status', render: (s: any) => s.enabled
                ? <Badge color="green">enabled</Badge>
                : <Badge color="gray">disabled</Badge> },
              { key: 'created', header: 'Added', render: (s: any) => new Date(s.created_at).toLocaleDateString() },
              { key: 'actions', header: '', render: (s: any) => (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/cameras/systems/${s.id}`)}>Manage</Button>
                </div>
              ) },
            ]}
            data={turingSystems.map((s: any) => ({ ...s, id: s.id }))}
          />
          </div>
        </Card>
      )}

      {unifiSystems.length > 0 && (
        <Card padding={false} className="mb-6 camera-systems-panel">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between"><div><h3 className="font-semibold">UniFi Protect Systems</h3><p className="text-xs text-gray-400 mt-0.5">Authorized Protect connections available for secure camera discovery and playback.</p></div><Badge color="blue">{unifiSystems.length} system{unifiSystems.length === 1 ? '' : 's'}</Badge></div>
          <div className="md:hidden grid grid-cols-1 gap-3 p-3">{unifiSystems.map((system: any) => { const systemCameras = cameras.filter(camera => camera.acs_provider === 'unifi_protect' && String(camera.property_id) === String(system.entity_id)); return <article key={system.id} className="customer-grid-card bg-white border border-gray-200"><div className="flex items-start justify-between mb-2 gap-2"><h3 className="font-semibold text-gray-900 truncate">{system.property_name || 'UniFi Protect'}</h3><Badge color={system.enabled !== false ? 'green' : 'gray'}>{system.enabled !== false ? 'enabled' : 'disabled'}</Badge></div><div className="customer-card-address"><span>Scope</span><p>{system.entity_type === 'property' ? system.property_name || 'Property' : 'Customer-wide'}</p></div><div className="customer-card-contact"><span>Provider</span><strong>UniFi Protect</strong><span>Cameras</span><strong>{cameraMetric(systemCameras.length)}</strong></div><div className="mobile-card-actions"><button onClick={() => setShowAuthorizedSetup(true)} className="row-open">Add cameras <span aria-hidden="true">→</span></button></div></article>; })}</div>
          <div className="hidden md:block"><Table columns={[
            { key: 'provider', header: 'Provider', render: () => <Badge color="blue">UniFi Protect</Badge> },
            { key: 'scope', header: 'Authorized scope', render: (system: any) => system.entity_type === 'property' ? system.property_name || system.entity_id : 'Customer-wide' },
            { key: 'enabled', header: 'Status', render: (system: any) => <Badge color={system.enabled !== false ? 'green' : 'gray'}>{system.enabled !== false ? 'enabled' : 'disabled'}</Badge> },
            { key: 'created', header: 'Added', render: (system: any) => system.created_at ? new Date(system.created_at).toLocaleDateString() : '—' },
            { key: 'actions', header: '', render: () => <Button size="sm" variant="secondary" onClick={() => setShowAuthorizedSetup(true)}>Add cameras</Button> },
          ]} data={unifiSystems.map(system => ({ ...system, id: system.id }))}/></div>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">{primaryInventoryUnavailable ? 'Camera inventory unavailable.' : 'No cameras found.'}</div>
          ) : paginated.map((c: any) => {
            const status = cameraConnectivity(c);
            const sc = status === 'online' ? 'bg-green-100 text-green-700' : status === 'offline' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700';
            const provider = c.acs_provider || (c._source === 'bridge' ? 'bridge' : c.manufacturer || 'camera');
            const type = c._source === 'bridge' ? 'Bridge Camera' : 'IP Camera';
            const system = c.acs_system_name || turingSystems.find((s: any) => String(s.id) === String(c.acs_system_id) || String(s.entity_id) === String(c.property_id))?.name || (c.acs_provider === 'turing' ? 'Turing Vision' : '—');
            const freshness = recordFreshness(c);
            return (
              <article key={c.id} className="prototype-mobile-card device-mobile-card">
                <header>
                  <div className="device-mobile-title">{deviceTypeIcon(c, status === 'offline')}<strong>{c.name || c.device_name || `Camera #${c.id}`}</strong></div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc}`}>{status}</span>
                </header>
                <dl className="device-mobile-details">
                  <div><dt>Provider</dt><dd><ProviderPill provider={provider} /></dd></div>
                  <div><dt>Type</dt><dd>{type}</dd></div>
                  <div className="wide"><dt>Property</dt><dd>{c._property_name || c.property_name || 'Unassigned'}</dd></div>
                  <div className="wide"><dt>System</dt><dd>{system}</dd></div>
                  <div className="wide"><dt>{freshness?.label || 'Freshness'}</dt><dd>{freshness ? new Date(freshness.value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not reported'}</dd></div>
                </dl>
                <div className="mobile-card-actions">
                  <button onClick={() => openCameraPreview(c)} className="row-open">Open <span aria-hidden="true">→</span></button>
                </div>
              </article>
            );
          })}
        </div>
        {filtered.length > 0 && <div className="mobile-pagination-card"><Pagination page={safePage} totalPages={totalPages} totalItems={filtered.length} startIndex={startIdx} endIndex={endIdx} onPageChange={setPage} /></div>}
      </div>

      {/* Desktop — grid view */}
      {viewMode === 'grid' && (
        <div className="hidden md:block">
          {primaryInventoryUnavailable && paginated.length === 0
            ? <div className="border border-red-300 bg-red-50 p-6 text-sm text-red-800">Camera inventory unavailable.</div>
            : <CameraGridView
                cameras={paginated}
                turingSystems={turingSystems}
                unifiSystems={unifiSystems}
                onTileClick={openCameraPreview}
              />}
          {!(primaryInventoryUnavailable && filtered.length === 0) && <div className="mt-4 bg-white rounded-xl border border-gray-200">
            <Pagination page={safePage} totalPages={totalPages} totalItems={filtered.length} startIndex={startIdx} endIndex={endIdx} onPageChange={setPage} />
            {filtered.length <= 20 && filtered.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-400 text-center">Showing all {filtered.length} cameras</div>
            )}
          </div>}
        </div>
      )}
      {/* Desktop — table view */}
      {viewMode === 'table' && (
      <Card padding={false} className="hidden md:block">
        <Table
          onRowClick={openCameraPreview}
          columns={[
            { key: 'name', header: 'Camera', render: (c: any) => <strong>{c.name || c.device_name || `Camera #${c.id}`}</strong> },
            { key: 'assigned', header: 'Assigned to', render: (c: any) => {
              const cameraId = String(c.id);
              const currentValue = pendingAssignment[cameraId] ?? String(c.property_id ?? '');
              const dirty = pendingAssignment[cameraId] !== undefined
                && pendingAssignment[cameraId] !== String(c.property_id ?? '');
              const saving = savingAssignment.has(cameraId);
              return (
                <div className="flex items-center gap-2">
                  <select
                    value={currentValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      const next = e.target.value;
                      setPendingAssignment((prev) => ({ ...prev, [cameraId]: next }));
                    }}
                    disabled={saving}
                    className={`text-sm border rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 max-w-[220px] ${dirty ? 'border-amber-400' : 'border-gray-200'}`}
                  >
                    {propertyList.length === 0 && <option value="">No properties</option>}
                    {propertyList.map((p: any) => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                  {dirty && (
                    <Button
                      size="sm"
                      onClick={(e: any) => { e.stopPropagation(); saveAssignment(cameraId); }}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  )}
                </div>
              );
            }},
            { key: 'provider', header: 'Provider', render: (c: any) => <ProviderPill provider={c.acs_provider || (c._source === 'bridge' ? 'bridge' : c.manufacturer || 'camera')} /> },
            { key: 'type', header: 'Type', render: (c: any) => <div className="device-type-visual">{deviceTypeIcon(c, cameraConnectivity(c) === 'offline')}<span>{c._source === 'bridge' ? 'Bridge Camera' : 'IP Camera'}</span></div> },
            { key: 'status', header: 'Online Status', render: (c: any) => {
              const state = cameraConnectivity(c);
              return state === 'unknown' ? <span className="prototype-status inactive" title="Live provider state unavailable">Unknown</span> : <div className="device-status-label"><DeviceStatusIcon online={state === 'online'}/><span>{state === 'online' ? 'Online' : 'Offline'}</span></div>;
            }},
            { key: 'last_seen', header: 'Freshness', render: (c: any) => {
              const freshness = recordFreshness(c);
              return freshness ? <span title={freshness.label}>{new Date(freshness.value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span> : 'Not reported';
            }},
            { key: 'view', header: '', render: (c: any) => (
              <DeviceActionButton
                onClick={(e: any) => { e.stopPropagation(); openCameraPreview(c); }}
              >
                View Stream
              </DeviceActionButton>
            )},
          ]}
          data={paginated}
          emptyMessage={primaryInventoryUnavailable ? 'Camera inventory unavailable' : 'No cameras found'}
        />
        {!(primaryInventoryUnavailable && filtered.length === 0) && <Pagination page={safePage} totalPages={totalPages} totalItems={filtered.length} startIndex={startIdx} endIndex={endIdx} onPageChange={setPage} />}
      </Card>
      )}

      {/* Camera Setup Wizard */}
      <CameraSetupWizard
        open={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        properties={propertyList}
        onCameraAdded={fetchCameras}
        onTuringChosen={() => setShowAddTuring(true)}
      />
      <AuthorizedResourceSetupModal open={showAuthorizedSetup} mode="camera" customers={customers} properties={propertyList} onClose={() => setShowAuthorizedSetup(false)} onSelect={selection => {
        if (selection.connection.integration_key === 'unifi_protect') setAuthorizedCameraSelection(selection);
        else if (selection.connection.integration_key === 'turing') setShowAddTuring(true);
      }} />
      <UniFiCameraSelectionModal
        selection={authorizedCameraSelection}
        properties={propertyList}
        propertyName={propertyList.find(property => String(property.id || property.uuid) === authorizedCameraSelection?.propertyId)?.name}
        onClose={() => setAuthorizedCameraSelection(null)}
        onComplete={async () => { await Promise.all([fetchCameras(), fetchUnifiSystems()]); setPageTab('cameras'); }}
      />

      {/* ONVIF onboarding (hardware-agnostic) */}
      {showOnvifModal && (
        <AddCameraModal
          open={showOnvifModal}
          onClose={() => setShowOnvifModal(false)}
          propertyId={onvifPropertyId}
          onCreated={fetchCameras}
        />
      )}

      {/* Live preview drawer (legacy ONVIF / bridge cameras) */}
      {previewId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewId(null)}>
          <div className="bg-black rounded-lg w-full max-w-3xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <CameraPlayer deviceId={previewId} className="w-full h-full" />
          </div>
        </div>
      )}

      {/* Turing Vision overlay — fetches a short-lived HLS URL via the
          backend so the access token never leaves the server. */}
      {turingPlay && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTuringPlay(null)}>
          <div className="bg-black rounded-lg w-full max-w-3xl aspect-video relative" onClick={(e) => e.stopPropagation()}>
            {turingPlay.error ? (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center">{turingPlay.error}</div>
            ) : turingPlay.streamUrl ? (
              <HlsVideo
                src={turingPlay.streamUrl}
                className="w-full h-full"
                onRefreshStreamUrl={async () => {
                  if (!turingPlay.systemId || !turingPlay.cameraId) return null;
                  try {
                    const isTuring = turingPlay.provider === 'turing';
                    const route = isTuring ? 'turing' : 'unifi-protect';
                    const r = await fetch(`${API_BASE}/integrations/${route}/systems/${turingPlay.systemId}/stream-url`, {
                      method: 'POST',
                      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify(isTuring ? { camera_id: turingPlay.cameraId, resolution: 'sub' } : { camera_id: turingPlay.cameraId, quality: 'low' }),
                    });
                    const data = await r.json();
                    return data.play_url ?? null;
                  } catch { return null; }
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Fetching stream URL…</div>
            )}
            <button className="absolute top-2 right-2 bg-white/10 text-white rounded px-2 py-1 text-xs" onClick={() => setTuringPlay(null)}>Close</button>
          </div>
        </div>
      )}

      <AddTuringSystemModal
        open={showAddTuring}
        onClose={() => setShowAddTuring(false)}
        properties={propertyList}
        onAdded={() => { setShowAddTuring(false); fetchTuringSystems(); }}
      />
      <Modal open={showViewEditor} onClose={() => setShowViewEditor(false)} title={activeSavedView ? 'Edit Camera View' : 'Create Camera View'} size="lg">
        <div className="camera-view-editor">{viewPersistenceError&&<div role="alert" className="vp-error">{viewPersistenceError}</div>}<label><span>View name</span><input value={viewName} onChange={e => setViewName(e.target.value)} placeholder="Example: Lobby and entrances" /></label><div className="camera-view-select"><header><strong>Choose cameras</strong><button onClick={() => setViewCameraIds(new Set(cameras.map(camera => String(camera.id))))}>Select all</button></header>{cameras.map(camera => { const id = String(camera.id); return <label key={id}><input type="checkbox" checked={viewCameraIds.has(id)} onChange={() => setViewCameraIds(current => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; })}/>{deviceTypeIcon(camera, !isOnline(camera))}<span><strong>{camera.name || camera.device_name || `Camera #${id}`}</strong><small>{camera._property_name || 'Unassigned'}</small></span></label>; })}</div><footer>{activeSavedView && <Button variant="danger" onClick={() => { const next = savedViews.filter(view => view.id !== activeSavedView.id); setSavedViews(next); setActiveViewId('all'); void persistCameraViews(next,'all'); setShowViewEditor(false); }}>Delete view</Button>}<Button variant="secondary" onClick={() => setShowViewEditor(false)}>Cancel</Button><Button disabled={!viewName.trim() || viewCameraIds.size === 0} onClick={() => { const id = activeSavedView?.id || crypto.randomUUID(); const record = { id, name:viewName.trim(), cameraIds:Array.from(viewCameraIds) }; const next = activeSavedView ? savedViews.map(view => view.id === id ? record : view) : [...savedViews, record]; setSavedViews(next); setActiveViewId(id); void persistCameraViews(next,id); setShowViewEditor(false); setPage(1); }}>Save view</Button></footer></div>
      </Modal>
    </div>
  );
};

// ─── Add Turing System modal ──────────────────────────────────────────
// Paste org access token from Turing Vision portal + pick a property.
// Token is sent to /integrations/turing/enable which KMS-encrypts it
// server-side. The browser only holds the token briefly while sending.
const AddTuringSystemModal: React.FC<{
  open: boolean;
  onClose: () => void;
  properties: any[];
  onAdded: () => void;
}> = ({ open, onClose, properties, onAdded }) => {
  const [propertyId, setPropertyId] = useState<string>('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPropertyId(properties[0]?.id ? String(properties[0].id) : '');
      setToken('');
      setError('');
    }
  }, [open, properties]);

  const submit = async () => {
    if (!propertyId || !token.trim()) { setError('Property and access token are required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/integrations/turing/enable`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_type: 'property',
          entity_id: propertyId,
          credentials: { access_token: token.trim() },
          settings: {},
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.slice(0, 240));
      }
      // Clear the token from local state ASAP — it's now KMS-encrypted server-side.
      setToken('');
      onAdded();
    } catch (e: any) {
      setError(String(e?.message ?? e).slice(0, 240));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Turing Vision System" size="md">
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          The access token is generated in the Turing Vision portal at
          <span className="font-mono"> Settings → Org Settings → Third-party Access Token</span>.
          Intrlock encrypts the token at rest with KMS and never sends it to client browsers or mobile apps.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Property</label>
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {properties.map((p: any) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
        </div>
        <Input
          label="Turing Access Token"
          type="password"
          value={token}
          onChange={(e: any) => setToken(e.target.value)}
          placeholder="paste token here"
          autoComplete="off"
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !token.trim() || !propertyId}>
            {saving ? 'Saving…' : 'Add System'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
